import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, RotateCw, Loader2 } from "lucide-react";
import type { Deck } from "@/types/linguadeck";
import { useIsMobile } from "@/hooks/use-mobile";
import { useWordFamily } from "@/hooks/useWordFamily";
import { extractRootWord } from "@/lib/philology/extractRootWord";
import { prefetchDeckFamilies } from "@/lib/philology/prefetchDeckFamilies";
import RootWordCard from "@/components/philology/RootWordCard";
import WordFamilyTree from "@/components/philology/WordFamilyTree";
import WordFamilyAccordion from "@/components/philology/WordFamilyAccordion";
import type { DerivativeRecord } from "@/lib/philology/api";

interface PhilologyModeProps {
  deck: Deck;
  cardIndex: number;
  onPrevCard?: () => void;
  onNextCard?: () => void;
  onSessionEnd?: (pct: number) => void;
}

type SideKey = "front" | "back";

export default function PhilologyMode({ deck, cardIndex, onPrevCard, onNextCard, onSessionEnd }: PhilologyModeProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const cards = deck.cards ?? [];
  const total = cards.length;

  // Local state mirrors the parent's cardIndex but lets internal buttons
  // update instantly without waiting for parent re-render (fixes mobile
  // tap-then-no-change bug — parent updateState was racing with re-render).
  const [localIndex, setLocalIndex] = useState(() =>
    Math.max(0, Math.min(cardIndex, Math.max(total - 1, 0)))
  );

  // Re-sync when parent cardIndex changes (e.g. external "open card N" deep link)
  // or when the deck/total changes.
  useEffect(() => {
    setLocalIndex(Math.max(0, Math.min(cardIndex, Math.max(total - 1, 0))));
  }, [cardIndex, total]);

  const safeIndex = Math.max(0, Math.min(localIndex, total - 1));
  const card = cards[safeIndex];

  const [side, setSide] = useState<SideKey>("front");
  const [activeWord, setActiveWord] = useState<string | null>(null);

  useEffect(() => {
    setSide("front");
    setActiveWord(null);
  }, [card?.id, safeIndex]);

  const handlePrev = useCallback(() => {
    if (safeIndex <= 0) return;
    setLocalIndex(safeIndex - 1);
    // Fire-and-forget parent sync so global state stays consistent.
    try { onPrevCard?.(); } catch { /* ignore */ }
  }, [safeIndex, onPrevCard]);

  const handleNext = useCallback(() => {
    if (safeIndex >= total - 1) return;
    setLocalIndex(safeIndex + 1);
    try { onNextCard?.(); } catch { /* ignore */ }
  }, [safeIndex, total, onNextCard]);

  // Background prefetch disabled — was burning Groq daily quota by warming up
  // 25-card decks (75k tokens each) on every Philology-tab open.
  // We rely on lazy on-demand fetch + Supabase shared cache instead.
  // To re-enable: uncomment below and ensure provider quotas allow it.
  // useEffect(() => {
  //   if (!cards.length) return;
  //   const ctrl = new AbortController();
  //   prefetchDeckFamilies(cards, deck.lang ?? "en", ctrl.signal).catch(() => undefined);
  //   return () => ctrl.abort();
  // }, [deck.id, cards, deck.lang]);

  // Use ref to avoid re-render loop when caller passes inline arrow function for onSessionEnd.
  const onSessionEndRef = useRef(onSessionEnd);
  useEffect(() => { onSessionEndRef.current = onSessionEnd; }, [onSessionEnd]);
  useEffect(() => {
    if (!total) return;
    const pct = Math.round(((safeIndex + 1) / total) * 100);
    onSessionEndRef.current?.(Math.min(100, pct));
  }, [safeIndex, total]);

  const { rootText, rootLang, sourcePhrase, hasBack } = useMemo(() => {
    if (!card) return { rootText: "", rootLang: deck.lang ?? "en", sourcePhrase: null as string | null, hasBack: false };
    const text = side === "front" ? card.front : (card.back ?? "");
    const lang = side === "front" ? (card.frontLang ?? deck.lang ?? "en") : (card.backLang ?? deck.lang ?? "en");
    const { rootWord, sourcePhrase } = extractRootWord(text ?? "", lang);
    return {
      rootText: rootWord,
      rootLang: lang,
      sourcePhrase,
      hasBack: !!card.back?.trim() && (card.backLang ?? card.frontLang) !== card.frontLang,
    };
  }, [card, side, deck.lang]);

  const queryEnabled = Boolean(card) && Boolean(rootText);
  const familyQuery = useWordFamily({
    word: rootText,
    language: rootLang,
    cardId: card?.id ?? null,
    sourcePhrase,
    enabled: queryEnabled,
  });

  if (!card) {
    return (
      <div className="w-full text-center py-12 text-[hsl(var(--muted-foreground))]">
        <p>{t("philology.empty")}</p>
      </div>
    );
  }

  function handleSelect(d: DerivativeRecord) {
    setActiveWord((prev) => (prev === d.word ? null : d.word));
  }

  const renderBody = () => {
    if (familyQuery.isLoading || (familyQuery.isFetching && !familyQuery.data)) {
      return (
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-[hsl(var(--muted-foreground))]">
          <Loader2 className="animate-spin" size={28} strokeWidth={2} />
          <p className="text-sm">{t("philology.loading")}</p>
        </div>
      );
    }
    if (familyQuery.isError) {
      return (
        <div className="text-center py-10 flex flex-col items-center gap-3">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("philology.error")}</p>
          <button
            type="button"
            onClick={() => familyQuery.refetch()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] hover:bg-[hsl(var(--border))]/40 text-sm font-medium transition-colors"
          >
            <RotateCw size={14} strokeWidth={2} />
            {t("philology.regenerate")}
          </button>
        </div>
      );
    }
    const derivatives = familyQuery.data?.derivatives ?? [];
    if (isMobile) {
      return <WordFamilyAccordion derivatives={derivatives} activeWord={activeWord} onSelect={handleSelect} />;
    }
    return <WordFamilyTree derivatives={derivatives} activeWord={activeWord} onSelect={handleSelect} />;
  };

  const meta = familyQuery.data?.metadata;

  return (
    <div className="w-full flex flex-col gap-4 md:gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-[hsl(var(--muted-foreground))]">
          {t("studyMode.progress", { current: safeIndex + 1, total })}
        </div>
        {hasBack && (
          <button
            type="button"
            onClick={() => setSide((s) => (s === "front" ? "back" : "front"))}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] hover:bg-[hsl(var(--border))]/40 text-xs font-medium transition-colors"
          >
            {t("philology.switchLanguage")}
          </button>
        )}
      </div>
      <div className="flex flex-col md:grid md:grid-cols-[minmax(260px,1fr)_2fr] gap-4 md:gap-6">
        <div className="md:sticky md:top-2 md:self-start">
          <RootWordCard
            rootWord={rootText}
            rootMeaning={meta?.rootMeaning ?? null}
            etymology={meta?.etymology ?? null}
            morphology={meta?.morphology ?? null}
            language={rootLang}
            level={deck.level ?? null}
            sourcePhrase={sourcePhrase}
          />
          {familyQuery.data?.status === "ready" && (
            <button
              type="button"
              onClick={() => familyQuery.regenerate()}
              className="mt-3 inline-flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
              aria-label={t("philology.regenerate")}
            >
              <RotateCw size={12} strokeWidth={2} />
              {t("philology.regenerate")}
            </button>
          )}
        </div>
        <div className="min-w-0">
          {renderBody()}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-2 pb-4">
        <button
          type="button"
          onClick={handlePrev}
          disabled={safeIndex === 0}
          className="inline-flex items-center justify-center min-w-[56px] min-h-[48px] px-5 rounded-xl border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] active:bg-[hsl(var(--bg3))] text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
          aria-label="Previous card"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>
        <span className="text-xs text-[hsl(var(--muted-foreground))] select-none">{safeIndex + 1} / {total}</span>
        <button
          type="button"
          onClick={handleNext}
          disabled={safeIndex >= total - 1}
          className="inline-flex items-center justify-center min-w-[56px] min-h-[48px] px-5 rounded-xl border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--bg3))] active:bg-[hsl(var(--bg3))] text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
          aria-label="Next card"
        >
          <ArrowRight size={20} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
